import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import LazyLoad from 'react-lazyload';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import Translator from '../../../layout/components/helpers/Translator';
import TotalEnergiesRiskPaginator from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/TotalEnergiesRiskPaginator';
import TotalEnergiesRiskSidebar from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Sidebar/TotalEnergiesRiskSidebar';
import ElementContainer from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/ElementContainer';
import AppLink from '../../../routing/helpers/AppLink';
import * as moduleAction from "../../../store/actions/moduleAction";

export default function ModuleTotalEnergiesRiskRiskListScreen() {
  const { moduleBridgeHex, filtersHex, page } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const risksPage = useSelector((state) => state.currentPage?.risksPage);
  const riskLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1
  );
  const [loading, setLoading] = useState(true);
  const [risks, setRisks] = useState(null);
  const [isLoadingExportReport, setIsLoadingExportReport] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > GENERATE REPORT
  const handleOnGenerateReport = useCallback(async () => {
    setIsLoadingExportReport(true);
    await axios
      .post(axiosPath('api_r_user_module_totalEnergiesRiskReport_xlsx1'), {
        moduleBridgeHex,
        filtersHex,
      })
      .then(({ data }) => {
        if (data.reportUrl) window.open(data.reportUrl, '_self');
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => setIsLoadingExportReport(false));
  }, [moduleBridgeHex, filtersHex, page]);
  // < GENERATE REPORT

  // > LOAD RISKS
  const loadRisks = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_totalEnergiesRisk_risk_list'), {
          moduleBridgeHex,
          filtersHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            setRisks(data);
            dispatch(currentPageAction.change('pagination', data.pagination));
            dispatch(
              currentPageAction.change('risksPage', data.elementIDs, true)
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, filtersHex, page]
  );

  useEffect(() => {
    setLoading(true);
    loadRisks();
  }, [filtersHex]);
  // < LOAD RISKS

  // > LOAD RISK LEVELS
  const loadRiskLevel = async (level) =>
    axios.post(axiosPath('api_r_user_module_totalEnergiesRisk_risk_level'), {
      moduleBridgeHex,
      elementIDs: risks?.elementIDs?.[page - 1],
      level,
    });

  // Level 1
  useEffect(() => {
    if (risks?.elementIDs) {
      setLoading(false);
      window.scrollTo(0, 0);
      if (risks?.elementIDs.length > 0) {
        loadRiskLevel(1).then((riskLvl1) => {
          dispatch(
            currentPageAction.change(
              'elementLevels',
              {
                level1: !riskLevel1
                  ? riskLvl1?.data
                  : { ...riskLevel1, ...riskLvl1?.data },
              },
              true
            )
          );
        });
      }
    }
  }, [risks, page]);
  // < LOAD RISK LEVELS

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath("api_r_user_module_totalEnergiesRisk_getModuleData"),
        "moduleData",
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "permissions",
          {
            moduleBridgeHex,
            subType: "permissions",
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "userBridge",
          {
            moduleBridgeHex,
            subType: "userBridge",
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);

  if (!moduleData) return <AppNoInfo />;

  return (
    <>
      <TotalEnergiesRiskSidebar activeRoute="user_module_totalEnergiesRisk_risk_list" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [{ name: moduleData?.moduleName }, { name: 'Riesgos' }]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {userBridge?.permissions?.level === 0 && (
            <div className="mb-2">
              <AppLink
                path="user_module_totalEnergiesRisk_risk_add"
                params={{
                  moduleBridgeHex,
                }}
                className="btn btn-xs btn-outline-light d-inline-flex mr-3"
              >
                Nuevo riesgo
              </AppLink>
              <button
                onClick={handleOnGenerateReport}
                type="button"
                className="btn btn-xs btn-outline-light d-inline-flex mr-3"
                disabled={isLoadingExportReport}
              >
                Exportar excel
              </button>
            </div>
          )}

          {filtersHex !== '0' && (
            <div className="alert alert-info pointer">
              <span
                onClick={() =>
                  navigate(
                    path({
                      name: 'user_module_totalEnergiesRisk_risk_list',
                      params: {
                        moduleBridgeHex,
                        page: 1,
                        filtersHex: '0',
                      },
                    })
                  )
                }
              >
                <AppRemixIcon icon="delete-bin-6" className="mr-2" />
                <Translator file="filter" name="filter.delete" />
              </span>
              {/* <span
                className="ml-4"
                onClick={() =>
                  dispatch(
                    currentPageAction.change('auxSidebar', {
                      opened: 'filters',
                    })
                  )
                }
              >
                <AppRemixIcon icon="equalizer" className="mr-2" />
                <Translator file="module-legalCompliance" name="show.filters" />
              </span> */}
            </div>
          )}
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : risksPage && risksPage.length > 0 ? (
            risksPage?.[page - 1].map((rID, i) => (
              <LazyLoad key={i} height={113}>
                <ElementContainer elementID={rID} type={moduleData?.type} />
              </LazyLoad>
            ))
          ) : (
            <div className="alert alert-info">Aún no existe ningún riesgo</div>
          )}
        </div>
        {risksPage?.length > 0 && risks && (
          <TotalEnergiesRiskPaginator
            pagination={risks?.pagination}
            route="user_module_totalEnergiesRisk_risk_list"
            page={page}
            plantObligationPages={risksPage}
            itemsPerPage={20}
            className="mt-3"
            params={{
              moduleBridgeHex,
              page,
              filtersHex,
            }}
          />
        )}
      </main>
    </>
  );
}
